import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, GraduationCap, KeyRound, CalendarClock } from "lucide-react";
import { Layout } from "../../components/layout/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Field, EmptyState, ProgressBar } from "../../components/common";
import { api, formatApiError } from "../../lib/api";
import { fmtDate, age, initials, todayISO } from "../../lib/format";
import { useAuth, hasPerm } from "../../context/AuthContext";

const EMPTY = { nom: "", prenom: "", date_naissance: "", date_inscription: "", date_debut_formation: "", date_fin_formation: "", email: "", telephone: "", notes: "" };

export function EleveFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, initial[k] || ""])) } : { ...EMPTY, date_inscription: todayISO() });
  }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f };
      ["date_naissance", "date_inscription", "date_debut_formation", "date_fin_formation"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      const { data } = initial ? await api.patch(`/eleves/${initial.id}`, payload) : await api.post("/eleves", payload);
      toast.success(initial ? "Fiche élève mise à jour" : "Fiche élève créée");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Modifier la fiche élève" : "Nouvelle fiche élève"}</DialogTitle>
          <DialogDescription>Les champs marqués * sont obligatoires.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="eleve-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nom" required><Input value={f.nom} onChange={set("nom")} required autoFocus data-testid="eleve-form-nom" /></Field>
            <Field label="Prénom" required><Input value={f.prenom} onChange={set("prenom")} required data-testid="eleve-form-prenom" /></Field>
            <Field label="Date de naissance"><Input type="date" value={f.date_naissance} onChange={set("date_naissance")} data-testid="eleve-form-naissance" /></Field>
            <Field label="Date d'inscription"><Input type="date" value={f.date_inscription} onChange={set("date_inscription")} data-testid="eleve-form-inscription" /></Field>
            <Field label="Date de début de formation"><Input type="date" value={f.date_debut_formation} onChange={set("date_debut_formation")} data-testid="eleve-form-debut" /></Field>
            <Field label="Date de fin de formation" hint="Facultatif — sert de fin d'accès par défaut pour le compte élève."><Input type="date" value={f.date_fin_formation} onChange={set("date_fin_formation")} /></Field>
            <Field label="E-mail"><Input type="email" value={f.email} onChange={set("email")} placeholder="pour les convocations et la fiche de suivi" /></Field>
            <Field label="Téléphone"><Input value={f.telephone} onChange={set("telephone")} /></Field>
          </div>
          <Field label="Notes internes"><Textarea rows={2} value={f.notes} onChange={set("notes")} /></Field>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="eleve-form-submit">{initial ? "Enregistrer" : "Créer la fiche"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function EleveListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [eleves, setEleves] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const canCreate = hasPerm(user, "eleves", "create");

  useEffect(() => { api.get("/eleves").then(({ data }) => setEleves(data)); }, []);
  const filtered = (eleves || []).filter((e) => `${e.prenom} ${e.nom} ${e.email || ""}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <Layout title="Élèves" actions={canCreate && <Button onClick={() => setOpen(true)} data-testid="eleve-create-button"><Plus size={16} /> <span className="hidden sm:inline">Nouvelle fiche</span></Button>}>
      <div className="relative max-w-md mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un élève..." className="pl-9" data-testid="eleve-search" />
      </div>
      {eleves === null ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-card">
          <EmptyState icon={GraduationCap} title={eleves.length ? "Aucun élève ne correspond" : "Aucune fiche élève"} text={eleves.length ? undefined : "Créez une fiche pour chaque élève puis ajoutez-y les modules suivis."}
            action={canCreate && !eleves.length ? <Button onClick={() => setOpen(true)}><Plus size={16} /> Nouvelle fiche</Button> : null} />
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="hidden md:grid grid-cols-[minmax(0,2fr)_1fr_1fr_minmax(0,1.4fr)_minmax(0,1.5fr)] gap-4 px-4 py-2.5 border-b border-border bg-muted/40 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <span>Élève</span><span>Inscription</span><span>Début formation</span><span>Progression</span><span>Prochain cours</span>
          </div>
          <ul className="divide-y divide-border">
            {filtered.map((e) => (
              <li key={e.id}>
                <button onClick={() => navigate(`/eleves/${e.id}`)} className="w-full text-left grid md:grid-cols-[minmax(0,2fr)_1fr_1fr_minmax(0,1.4fr)_minmax(0,1.5fr)] gap-x-4 gap-y-2 items-center px-4 py-3 hover:bg-accent/50 transition-colors" data-testid={`eleve-row-${e.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-9 w-9 shrink-0 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">{initials(e.prenom, e.nom)}</span>
                    <div className="min-w-0">
                      <p className="font-medium truncate flex items-center gap-1.5">{e.prenom} {e.nom} {e.has_account && <KeyRound size={13} className="text-muted-foreground shrink-0" aria-label="Accès élève" />}</p>
                      <p className="text-xs text-muted-foreground truncate">{e.date_naissance ? `${age(e.date_naissance)} ans` : ""}{e.date_naissance && e.email ? " · " : ""}{e.email}</p>
                    </div>
                  </div>
                  <span className="text-sm text-muted-foreground hidden md:block">{fmtDate(e.date_inscription)}</span>
                  <span className="text-sm text-muted-foreground hidden md:block">{fmtDate(e.date_debut_formation)}</span>
                  <div className="flex items-center gap-2">
                    <ProgressBar percent={e.progress.percent} className="flex-1" />
                    <span className="text-xs tabular-nums text-muted-foreground w-24 text-right">{e.progress.done}/{e.progress.total} · {e.progress.modules} mod.</span>
                  </div>
                  <span className="text-xs text-muted-foreground truncate flex items-center gap-1">
                    {e.prochain_cours ? <><CalendarClock size={13} className="shrink-0" />{fmtDate(e.prochain_cours.date)} {e.prochain_cours.heure_debut} · {e.prochain_cours.module_nom}</> : "Aucun cours prévu"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <EleveFormDialog open={open} onOpenChange={setOpen} onSaved={(d) => navigate(`/eleves/${d.id}`)} />
    </Layout>
  );
}
